import { useEffect, useRef, useState } from 'react';

export type QuoteBarState = 'open' | 'closed_pending' | 'historical_pending' | 'unknown';
const cst = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
export function beijingClock(now: Date): { day: string; afterClose: boolean } {
  const parts = cst.formatToParts(now), part = (name: string) => parts.find(p => p.type === name)!.value;
  return { day: `${part('year')}-${part('month')}-${part('day')}`, afterClose: Number(part('hour')) >= 15 };
}
/** Only publish a new React state at a close/date boundary; unrelated rerenders do not rebuild bars. */
export function useBeijingQuoteClock() {
  const key = () => { const c = beijingClock(new Date()); return `${c.day}/${c.afterClose ? 'closed' : 'open'}`; };
  const [value, setValue] = useState(key);
  const last = useRef(value);
  useEffect(() => {
    const refresh = () => { const next = key(); if (next !== last.current) { last.current = next; setValue(next); } };
    const timer = window.setInterval(refresh, 10000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, []);
  const [day, phase] = value.split('/');
  return { day, afterClose: phase === 'closed' };
}
export function quoteBarState(day: string | undefined, clock: {day: string; afterClose: boolean}): QuoteBarState {
  if (!day || day > clock.day) return 'unknown';
  if (day < clock.day) return 'historical_pending';
  return clock.afterClose ? 'closed_pending' : 'open';
}
export function quoteBarLabel(bar: { quote_state?: QuoteBarState }): string {
  return ({ open: '今日·未收盘', closed_pending: '收盘待确认', historical_pending: '历史报价待确认', unknown: '报价待确认' })[bar.quote_state ?? 'unknown'];
}
export function quoteBarExplanation(bar: {quote_state?: QuoteBarState}): string {
  return bar.quote_state === 'open' ? '今日未收盘，读数会变。' : '报价补位，日线底座尚未确认；不作为已确认收盘数据。';
}
/** Naive timestamps from the API are Beijing time. A database date placeholder is not a quote clock. */
export function quoteTimestamp(timestamp: string | null | undefined): { day: string | null; time: string | null } {
  if (!timestamp || !/^\d{4}-\d{2}-\d{2}/.test(timestamp)) return {day: null, time: null};
  const hasTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(timestamp);
  if (!hasTime) return {day: timestamp.slice(0, 10), time: null};
  const zoned = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
  if (!zoned && timestamp.slice(11,16) === '00:00') return {day: timestamp.slice(0,10), time: null};
  const date = new Date(timestamp.replace(' ', 'T') + (zoned ? '' : '+08:00'));
  if (!Number.isFinite(date.valueOf())) return {day: timestamp.slice(0,10), time: null};
  const parts = cst.formatToParts(date), part = (name: string) => parts.find(p => p.type === name)!.value;
  return {day: `${part('year')}-${part('month')}-${part('day')}`, time: `${part('hour')}:${part('minute')}`};
}
export function quoteSourceLabel(source?: string): string {
  if (source === 'sina_hk_public') return '新浪港股公开报价';
  if (source === 'tencent_hk_public') return '腾讯港股公开报价';
  if (source === 'database') return '数据库报价';
  if (source === 'ths_native') return '同花顺报价';
  if (source === 'sina_dated_quote') return '新浪报价';
  return source === 'realtime' ? '实时报价' : '报价';
}

export function quoteTimeLabel(time: string | null, kind?: string): string {
  if (!time) return '报价时刻未提供';
  return `${kind === 'provider_response_minute_not_exchange_trade' ? '源端响应时间' : '报价时间'}（北京时间）${time}`;
}
